import { Vue, Component } from 'vue-property-decorator';
import { beforeDestroy, mounted } from '@/Utility/Decorators';

/**
 * Mixin that provides automatic scroll-to-load functionality for collections
 *
 * @package Mixins
 * @project ChalkySticks App
 */
@Component
export class AutoScroll extends Vue {
	/**
	 * Flag to prevent multiple simultaneous fetch requests
	 * @type boolean
	 */
	private isFetchingMore: boolean = false;

	/**
	 * Distance from bottom to trigger loading
	 * @type number
	 */
	protected scrollThreshold: number = 200;

	/**
	 * Attaches scroll observer for infinite loading
	 *
	 * The name isn't attachEvents because it's a mixin that might
	 * override other 'attachEvents' methods in the component
	 *
	 * @return void
	 */
	@mounted
	protected attachAutoScrollObserver(): void {
		if (this.$el) {
			this.$el.addEventListener('scroll', this.Handle_OnAutoScroll, { passive: true });
		}
	}

	/**
	 * Removes scroll observer
	 *
	 * The name isn't detachEvents because it's a mixin that might
	 * override other 'detachEvents' methods in the component
	 *
	 * @return void
	 */
	@beforeDestroy
	protected detachAutoScrollObserver(): void {
		if (this.$el) {
			this.$el.removeEventListener('scroll', this.Handle_OnAutoScroll);
		}
	}

	/**
	 * Checks if user has scrolled near the bottom
	 *
	 * @return boolean
	 */
	protected isNearBottom(): boolean {
		if (!this.$el) return false;

		const scrollTop = this.$el.scrollTop;
		const elementHeight = this.$el.clientHeight;
		const scrollHeight = this.$el.scrollHeight;

		return scrollTop + elementHeight >= scrollHeight - this.scrollThreshold;
	}

	/**
	 * Triggers loading more content - override in component
	 *
	 * @return Promise<void>
	 */
	protected async triggerLoadMore(): Promise<void> {
		console.warn('triggerLoadMore not implemented');
	}

	// region: Event handlers
	// ---------------------------------------------------------------------------

	/**
	 * @param Event e
	 * @return Promise<void>
	 */
	protected async Handle_OnAutoScroll(e: Event): Promise<void> {
		if (this.isNearBottom() && !this.isFetchingMore) {
			this.isFetchingMore = true;
			await this.triggerLoadMore();
			this.isFetchingMore = false;
		}
	}

	// endregion: Event handlers
}
